<script lang="ts" setup>
import type { RouteLocationRaw } from "vue-router";
import type { Breadcrumb } from "~/core/models/Breadcrumb.interface";
import ILxcArrowLeft from "~icons/lxc/arrow-left";

const props = defineProps<{
  /**
   * string to display as breadcrumb.
   * If not provided, it uses the meta breadcrumb defined on the route.
   */
  name?: string;

  /**
   * If the name is a string, true to not translate it.
   */
  shouldNotTranslate?: boolean;

  /**
   * The route to go back.
   * The back button is displayed if this property is defined.
   */
  backRoute?: RouteLocationRaw;

  /**
   * The default route to go back if the back route is not provided and the previous page does not exist.
   * The back button is displayed if this property is defined.
   */
  backDefaultRoute?: RouteLocationRaw;

  /**
   * List of the routes to exclude for the back button.
   * If the back route is among the excluded ones, the default back route is used.
   */
  backExcludeRoutes?: RouteLocationRaw[];
}>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const breadcrumb = route.meta.breadcrumb as Breadcrumb;

const title = computed(() => {
  if (props.name !== undefined) {
    return props.shouldNotTranslate ? props.name : t(props.name);
  } else {
    return t(breadcrumb.title);
  }
});

async function goBack() {
  if (props.backRoute && !props.backExcludeRoutes?.includes(props.backRoute)) {
    await router.push(props.backRoute);
  } else {
    const previousPage = localStorage.getItem("previousPage");
    if (previousPage && !props.backExcludeRoutes?.includes(previousPage)) {
      await router.push(previousPage);
    } else if (props.backDefaultRoute) {
      await router.push(props.backDefaultRoute);
    } else {
      router.back();
    }
  }
}

onMounted(() => {
  if (
    !localStorage.getItem("previousPage") &&
    router.options.history.state.back
  ) {
    localStorage.setItem(
      "previousPage",
      router.options.history.state.back.toString(),
    );
  }
});

onUnmounted(() => {
  localStorage.removeItem("previousPage");
});

defineExpose({
  goBack,
});
</script>

<template>
  <div class="h-11 flex justify-start items-center">
    <lxc-button
      v-if="backRoute || backDefaultRoute"
      type="tertiary"
      :title="t('button.back')"
      class="h-11 w-11 mr-4"
      @click="goBack"
    >
      <i-lxc-arrow-left class="w-5 h-5 text-gray-700" />
    </lxc-button>

    <h2 class="text-3xl font-semibold text-gray-900 my-4">
      <slot name="default">
        {{ title }}
      </slot>
    </h2>
  </div>
</template>
